import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import {
  VENDOR_PLATFORMS,
  CUSTOMER_PLATFORMS,
  TENANT_CORE,
  type Platform,
} from "../content/platforms";

/**
 * PlatformsPage — rebuilt on the design system. (CO 069 + CO 071 + CO 109)
 *
 * CO 071: the TENANT is the spine, not retail. The tenant-core is the base
 * every business carries; the platforms compose on top.
 * CO 109: platforms split by posture — VENDOR (a kind of business you become:
 * store, dentist, kitchen, auto shop) vs CUSTOMER (tools you use to run it:
 * email, shipping, marketing, payments). Kept visually separate so a non-retail
 * visitor finds itself, and so a tool is never mistaken for "what your business
 * is." (CO 032-004)
 */
const HERO = PAGE_HEROES['platforms']!;

export function PlatformsPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Every ComOS tenant starts with a complete core — identity,
              accounts, agents, governance, voice, knowledge. Then you pick what
              your business is — a store, a dentist, a kitchen, an auto shop —
              and add the tools to run it. Add what you are, not what we assume.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* The spine: the tenant-core */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="The spine"
            title="What every tenant is — before it sells anything."
            lead="A tenant with zero platforms composed is still a complete tenant. This is the core; the platforms compose on top of it."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {TENANT_CORE.map((c) => (
              <motion.div key={c.name} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-1.5">{c.name}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {c.blurb}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Vendor platforms — money IN. The tenant is the SELLER. */}
      <Section>
        <Container>
          <SectionHead
            eyebrow="Where you make money · you're the seller"
            title="Be a store. Or a dentist. Or a kitchen."
            lead="These are the kinds of business you can become — the face you sell on, where money comes in. Pick one or several; the same tenant can be more than one. None is required, and retail is just the first one most people reach for."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-2"
          >
            {VENDOR_PLATFORMS.map((p) => (
              <motion.div key={p.slug} variants={fadeUp}>
                <PlatformCard platform={p} />
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Customer platforms — money OUT. The tenant is the CONSUMER. */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="Where you spend to run it · you're the customer"
            title="The tools you buy to run the business."
            lead="These are the tools you spend on to run a business: email, shipping, marketing, payments, support. Today each has one provider, so here you're simply the customer. But every one of these is a market — and as it opens up, you could become a provider yourself, if you clear its vetting. Each replaces an entire category of SaaS and is callable by an AI agent the moment you turn it on."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-2"
          >
            {CUSTOMER_PLATFORMS.map((p) => (
              <motion.div key={p.slug} variants={fadeUp}>
                <PlatformCard platform={p} />
              </motion.div>
            ))}
          </motion.div>

          {/* Light-touch truth: the line between "sell on" and "spend on" is
              admittance, not architecture. (CO 032-004, Ron 2026-06-13) */}
          <motion.p
            {...inView}
            variants={fadeUp}
            className="mt-10 max-w-2xl text-sm text-mute dark:text-mute-soft"
          >
            There's no hard line between the two sides. Every platform is a
            market, and any tenant can apply to become a provider on one — the
            only thing in the way is that platform's vetting, which rises with
            the damage a bad actor could do (a rogue email sender hurts
            everyone, so email's bar is the highest). Today the tools above have
            a single provider; as each market opens, “spend on it” can become
            “sell on it.”
          </motion.p>
        </Container>
      </Section>

      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              Add what your business becomes.
            </h2>
            <p className="text-lead text-paper/70 mt-6">
              One tenant. Every platform. Every agent. Set it up free.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Start building — free
              </Link>
              <Link
                to="/pricing"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                How it's priced
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}

function PlatformCard({ platform: p }: { platform: Platform }) {
  return (
    <Card hover className="h-full flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-display-sm">{p.name}</h3>
        {p.status !== "available" && (
          <span className="rounded-pill border border-mute-line dark:border-ink-line px-2 py-0.5 text-[11px] uppercase tracking-wide text-mute dark:text-mute-soft">
            {p.status === "preset" ? "Preset" : "Coming soon"}
          </span>
        )}
      </div>
      <p className="text-sm text-signal dark:text-signal-bright mb-2">
        {p.tagline}
      </p>
      <p className="text-mute dark:text-mute-soft text-sm flex-1">{p.blurb}</p>
      <div className="mt-4">
        <PlatformMeta platform={p} />
        <Link
          to={`/platforms/${p.slug}`}
          className="mt-3 inline-block text-sm text-signal dark:text-signal-bright underline underline-offset-2"
        >
          Details →
        </Link>
      </div>
    </Card>
  );
}

function PlatformMeta({ platform }: { platform: Platform }) {
  // vendor platforms describe a business kind, not a SaaS replacement; coming-soon
  // ones have no live agent verb yet. Render only what the platform actually has.
  if (!platform.replaces && !platform.agentCall) return null;
  return (
    <div className="text-xs text-mute dark:text-mute-soft">
      {platform.replaces && (
        <>
          <span className="font-grotesk font-semibold">Replaces </span>
          {platform.replaces.slice(0, 3).join(", ")}
        </>
      )}
      {platform.agentCall && (
        <code className="mt-2 block rounded bg-mute-surface dark:bg-ink-line text-signal dark:text-signal-bright font-mono px-2 py-1 break-words">
          {platform.agentCall}
        </code>
      )}
    </div>
  );
}
